export interface UploadErrorGroup {
  requestId: string;
  rowErrors: string[];
}

/**
 * Parses the flat errors[] array from BulkUploadResult into per-request groups.
 *
 * The BE produces two error formats:
 *   1. Two-line pattern (CSV validation failures):
 *        "row N: <reason>"                          ← row-level, may repeat
 *        "request X skipped: N invalid row(s)"      ← terminates the group
 *   2. Single-line pattern (business-rule failures):
 *        "request X: <reason>"
 */
export function parseUploadErrors(errors: string[]): UploadErrorGroup[] {
  const groups: UploadErrorGroup[] = [];
  let pendingRowErrors: string[] = [];

  for (const line of errors) {
    const skippedMatch = line.match(/^request (\S+) skipped:/);
    if (skippedMatch) {
      groups.push({ requestId: skippedMatch[1], rowErrors: pendingRowErrors });
      pendingRowErrors = [];
      continue;
    }

    const directMatch = line.match(/^request (\S+):\s+(.+)/);
    if (directMatch) {
      groups.push({ requestId: directMatch[1], rowErrors: [directMatch[2]] });
      continue;
    }

    // Row-level error — accumulate until the matching "skipped" line appears.
    pendingRowErrors.push(line);
  }

  // Flush any trailing row errors that arrived without a closing "skipped" line.
  if (pendingRowErrors.length > 0) {
    groups.push({ requestId: "?", rowErrors: pendingRowErrors });
  }

  return groups;
}

/** Formats a number as Indian Rupee currency, always with 2 decimal places. */
export function formatAmount(amount: number): string {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

/** Formats an ISO date string as "DD Mon YYYY". Returns "—" for null/undefined. */
export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
